import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { transcriptions } from '../../services/api';
import './ProgressPage.css';

/**
 * ProgressPage Component
 *
 * Comprehensive language learning progress dashboard showing:
 * - Overall learning stats (proficiency, streak, time spent)
 * - Vocabulary progress (flashcards created, reviewed, due)
 * - Conversation practice stats
 * - Weekly activity chart
 * - Quick actions for next steps
 */
const ProgressPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pastMeetings, setPastMeetings] = useState([]);
  const [loadingMeetings, setLoadingMeetings] = useState(true);
  const [expandedMeetingId, setExpandedMeetingId] = useState(null);

  const languageId = searchParams.get('languageId');

  // Fetch past meetings/transcriptions
  useEffect(() => {
    const fetchPastMeetings = async () => {
      try {
        setLoadingMeetings(true);
        const response = await transcriptions.getAll({ languageId });
        console.log('✅ Fetched transcriptions:', response.data);

        // Filter by languageId (client-side) and sort by most recent first
        const meetings = (response.data || [])
          .filter(meeting => !languageId || meeting.languageId === languageId)
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

        console.log(`📊 Filtered ${meetings.length} meetings${languageId ? ` for language ${languageId}` : ' (all languages)'}`);
        setPastMeetings(meetings);
      } catch (error) {
        console.error('❌ Failed to fetch transcriptions:', error);
        setPastMeetings([]);
      } finally {
        setLoadingMeetings(false);
      }
    };

    if (user) {
      fetchPastMeetings();
    }
  }, [user, languageId]);

  useEffect(() => {
    // TODO: Fetch real stats from backend
    // For now, use mock data
    const mockStats = {
      language: 'Spanish',
      proficiencyLevel: 'Intermediate',
      studyStreak: 7,
      daysStudied: 23,
      totalTimeMinutes: 1450, // ~24 hours

      vocabulary: {
        totalFlashcards: 156,
        reviewedToday: 23,
        dueForReview: 12,
        masteredCards: 87,
        newWordsThisWeek: 34,
      },

      conversations: {
        totalConversations: 18,
        conversationsThisWeek: 5,
        totalMinutes: 340,
        averageDurationMinutes: 19,
      },

      weeklyActivity: [
        { day: 'Mon', minutes: 45, sessions: 3 },
        { day: 'Tue', minutes: 60, sessions: 4 },
        { day: 'Wed', minutes: 30, sessions: 2 },
        { day: 'Thu', minutes: 55, sessions: 3 },
        { day: 'Fri', minutes: 40, sessions: 2 },
        { day: 'Sat', minutes: 25, sessions: 1 },
        { day: 'Sun', minutes: 50, sessions: 3 },
      ],

      achievements: [
        { id: 1, name: '7-Day Streak', icon: '🔥', unlocked: true },
        { id: 2, name: '100 Flashcards', icon: '🗂️', unlocked: true },
        { id: 3, name: '10 Conversations', icon: '💬', unlocked: true },
        { id: 4, name: '20 Hours Study', icon: '⏱️', unlocked: true },
        { id: 5, name: '30-Day Streak', icon: '🏆', unlocked: false },
        { id: 6, name: '500 Flashcards', icon: '📚', unlocked: false },
      ],
    };

    setTimeout(() => {
      setStats(mockStats);
      setLoading(false);
    }, 500);
  }, []);

  const formatTime = (minutes) => {
    if (!minutes || isNaN(minutes)) return '0m';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const getMasteryPercentage = () => {
    if (!stats) return 0;
    return Math.round((stats.vocabulary.masteredCards / stats.vocabulary.totalFlashcards) * 100);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const handleMeetingClick = async (meeting) => {
    // Toggle expanded state
    if (expandedMeetingId === meeting.id) {
      setExpandedMeetingId(null);
      return;
    }

    // Expand this meeting
    setExpandedMeetingId(meeting.id);

    // Fetch full analysis if not already loaded
    if (!meeting.analysis) {
      try {
        const analysisResponse = await transcriptions.getAnalysis(meeting.id);

        // Update the meeting in the list with the analysis
        setPastMeetings(prev => prev.map(m =>
          m.id === meeting.id
            ? { ...m, analysis: analysisResponse.data }
            : m
        ));
      } catch (error) {
        console.error('Failed to fetch analysis:', error);
      }
    }
  };

  if (loading || !stats) {
    return (
      <div className="progress-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading your progress...</p>
        </div>
      </div>
    );
  }

  const maxActivityMinutes = Math.max(...stats.weeklyActivity.map(d => d.minutes));

  return (
    <div className="progress-page">
      {/* Header */}
      <header className="progress-header">
        <button className="btn-back" onClick={() => navigate('/home')}>
          ← Back
        </button>
        <h1>Your Learning Progress</h1>
      </header>

      {/* Past Meetings Section */}
      <section className="past-meetings-section">
        <h2>📚 Past Classes</h2>

        {loadingMeetings ? (
          <div className="meetings-loading">
            <div className="spinner-small"></div>
            <p>Loading your classes...</p>
          </div>
        ) : pastMeetings.length === 0 ? (
          <div className="no-meetings">
            <p>No classes recorded yet. Start a meeting and record it to see your progress here!</p>
          </div>
        ) : (
          <div className="meetings-list">
            {pastMeetings.map((meeting, index) => {
              // Check if this meeting is still processing
              const isProcessing = meeting.status === 'processing';

              const isExpanded = expandedMeetingId === meeting.id;

              return (
                <div
                  key={meeting.id}
                  className={`meeting-container ${isProcessing ? 'processing' : ''} ${index === 0 ? 'most-recent' : ''} ${isExpanded ? 'expanded' : ''}`}
                >
                  {/* Collapsed Row - Click to Expand */}
                  <div
                    className="meeting-row"
                    onClick={() => handleMeetingClick(meeting)}
                  >
                    <div className="meeting-icon">
                      {isProcessing ? '⏳' : '🎓'}
                    </div>

                    <div className="meeting-info">
                      <div className="meeting-header">
                        <h3 className="meeting-title">
                          {isProcessing ? 'Processing class...' : `Class Session`}
                        </h3>
                        <span className="meeting-time">{formatTime(meeting.duration)}</span>
                      </div>

                      <div className="meeting-meta">
                        <span className="meeting-date">{formatDate(meeting.createdAt)}</span>
                        <span className="meeting-separator">•</span>
                        <span className="meeting-language">
                          {meeting.language === 'es' ? '🇪🇸 Spanish' :
                           meeting.language === 'fr' ? '🇫🇷 French' :
                           meeting.language === 'de' ? '🇩🇪 German' :
                           String(meeting.language || 'Unknown')}
                        </span>
                        {meeting.category && (
                          <>
                            <span className="meeting-separator">•</span>
                            <span className="meeting-category">{String(meeting.category)}</span>
                          </>
                        )}
                      </div>

                      {isProcessing && (
                        <div className="processing-indicator">
                          <div className="processing-bar">
                            <div className="processing-fill"></div>
                          </div>
                          <p>Transcribing audio and generating flashcards...</p>
                        </div>
                      )}

                      {!isProcessing && !isExpanded && meeting.text && typeof meeting.text === 'string' && (
                        <p className="meeting-preview">
                          {meeting.text.substring(0, 150)}
                          {meeting.text.length > 150 ? '...' : ''}
                        </p>
                      )}
                    </div>

                    <div className="meeting-arrow">
                      {isExpanded ? '▼' : '→'}
                    </div>
                  </div>

                  {/* Expanded Content - 3 Sections */}
                  {isExpanded && meeting.analysis && (
                    <div className="meeting-expanded">
                      {/* Section 1: Summary */}
                      <div className="expanded-section summary-section">
                        <div className="section-header">
                          <span className="section-icon">📝</span>
                          <h4>Summary</h4>
                        </div>
                        <div className="section-content">
                          <p>{meeting.analysis.summary || 'No summary available yet.'}</p>
                        </div>
                      </div>

                      {/* Section 2: Notes (What You Learned) */}
                      <div className="expanded-section notes-section">
                        <div className="section-header">
                          <span className="section-icon">💡</span>
                          <h4>What You Learned</h4>
                        </div>
                        <div className="section-content">
                          <p>{meeting.analysis.whatLearned || 'Processing learning insights...'}</p>
                        </div>
                      </div>

                      {/* Section 3: Flashcards */}
                      <div className="expanded-section flashcards-section">
                        <div className="section-header">
                          <span className="section-icon">🗂️</span>
                          <h4>Flashcards ({meeting.analysis.suggestedFlashcards?.length || 0})</h4>
                        </div>
                        <div className="section-content">
                          {meeting.analysis.suggestedFlashcards && meeting.analysis.suggestedFlashcards.length > 0 ? (
                            <div className="flashcards-grid">
                              {meeting.analysis.suggestedFlashcards.map((card, idx) => (
                                <div key={idx} className="flashcard-preview">
                                  <div className="flashcard-front">
                                    <strong>Q:</strong> {card.front}
                                  </div>
                                  <div className="flashcard-back">
                                    <strong>A:</strong> {card.back}
                                  </div>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="no-flashcards">No flashcards generated yet.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Loading State for Expanded Content */}
                  {isExpanded && !meeting.analysis && (
                    <div className="meeting-expanded loading">
                      <div className="spinner-small"></div>
                      <p>Loading class details...</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Hero Stats */}
      <section className="hero-stats">
        <div className="stat-card primary">
          <div className="stat-icon">🇪🇸</div>
          <div className="stat-content">
            <h3>{stats.language}</h3>
            <p className="stat-label">Learning</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>{stats.proficiencyLevel}</h3>
            <p className="stat-label">Proficiency Level</p>
          </div>
        </div>

        <div className="stat-card highlight">
          <div className="stat-icon">🔥</div>
          <div className="stat-content">
            <h3>{stats.studyStreak} days</h3>
            <p className="stat-label">Current Streak</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon">⏱️</div>
          <div className="stat-content">
            <h3>{formatTime(stats.totalTimeMinutes)}</h3>
            <p className="stat-label">Total Study Time</p>
          </div>
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="progress-grid">
        {/* Vocabulary Progress */}
        <section className="progress-section vocabulary-section">
          <h2>📚 Vocabulary Progress</h2>

          <div className="mastery-bar">
            <div className="mastery-label">
              <span>Mastery Level</span>
              <span className="mastery-percentage">{getMasteryPercentage()}%</span>
            </div>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${getMasteryPercentage()}%` }}
              ></div>
            </div>
            <p className="mastery-subtext">
              {stats.vocabulary.masteredCards} of {stats.vocabulary.totalFlashcards} cards mastered
            </p>
          </div>

          <div className="vocab-stats">
            <div className="vocab-stat">
              <div className="vocab-stat-value">{stats.vocabulary.totalFlashcards}</div>
              <div className="vocab-stat-label">Total Flashcards</div>
            </div>
            <div className="vocab-stat">
              <div className="vocab-stat-value">{stats.vocabulary.reviewedToday}</div>
              <div className="vocab-stat-label">Reviewed Today</div>
            </div>
            <div className="vocab-stat highlight-stat">
              <div className="vocab-stat-value">{stats.vocabulary.dueForReview}</div>
              <div className="vocab-stat-label">Due for Review</div>
            </div>
            <div className="vocab-stat">
              <div className="vocab-stat-value">+{stats.vocabulary.newWordsThisWeek}</div>
              <div className="vocab-stat-label">New This Week</div>
            </div>
          </div>

          {stats.vocabulary.dueForReview > 0 && (
            <button
              className="btn-action btn-review"
              onClick={() => navigate('/flashcards')}
            >
              Review {stats.vocabulary.dueForReview} Cards Now
            </button>
          )}
        </section>

        {/* Conversation Practice */}
        <section className="progress-section conversation-section">
          <h2>💬 Conversation Practice</h2>

          <div className="conversation-stats">
            <div className="conversation-stat">
              <div className="stat-number">{stats.conversations.totalConversations}</div>
              <div className="stat-text">Total Conversations</div>
            </div>
            <div className="conversation-stat">
              <div className="stat-number">{stats.conversations.conversationsThisWeek}</div>
              <div className="stat-text">This Week</div>
            </div>
            <div className="conversation-stat">
              <div className="stat-number">{formatTime(stats.conversations.totalMinutes)}</div>
              <div className="stat-text">Total Practice</div>
            </div>
            <div className="conversation-stat">
              <div className="stat-number">{stats.conversations.averageDurationMinutes}m</div>
              <div className="stat-text">Avg. Duration</div>
            </div>
          </div>

          <button
            className="btn-action btn-conversation"
            onClick={() => navigate('/conversations')}
          >
            Start Practice Conversation
          </button>
        </section>

        {/* Weekly Activity Chart */}
        <section className="progress-section activity-section">
          <h2>📈 Weekly Activity</h2>

          <div className="activity-chart">
            {stats.weeklyActivity.map((day, index) => (
              <div key={index} className="activity-day">
                <div className="activity-bar-container">
                  <div
                    className="activity-bar"
                    style={{
                      height: `${(day.minutes / maxActivityMinutes) * 100}%`,
                      minHeight: day.minutes > 0 ? '10px' : '2px'
                    }}
                    title={`${day.minutes} minutes, ${day.sessions} sessions`}
                  ></div>
                </div>
                <div className="activity-label">{day.day}</div>
                <div className="activity-minutes">{day.minutes}m</div>
              </div>
            ))}
          </div>

          <div className="activity-summary">
            <p>
              <strong>{stats.weeklyActivity.reduce((sum, d) => sum + d.minutes, 0)} minutes</strong>
              {' '}this week across{' '}
              <strong>{stats.weeklyActivity.reduce((sum, d) => sum + d.sessions, 0)} sessions</strong>
            </p>
          </div>
        </section>

        {/* Achievements */}
        <section className="progress-section achievements-section">
          <h2>🏆 Achievements</h2>

          <div className="achievements-grid">
            {stats.achievements.map((achievement) => (
              <div
                key={achievement.id}
                className={`achievement ${achievement.unlocked ? 'unlocked' : 'locked'}`}
              >
                <div className="achievement-icon">{achievement.icon}</div>
                <div className="achievement-name">{achievement.name}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Quick Actions */}
        <section className="progress-section quick-actions-section">
          <h2>⚡ Quick Actions</h2>

          <div className="quick-actions">
            <button
              className="quick-action-btn"
              onClick={() => navigate('/flashcards')}
            >
              <span className="action-icon">🗂️</span>
              <span className="action-text">Review Flashcards</span>
            </button>

            <button
              className="quick-action-btn"
              onClick={() => navigate('/conversations')}
            >
              <span className="action-icon">💬</span>
              <span className="action-text">Practice Conversation</span>
            </button>

            <button
              className="quick-action-btn"
              onClick={() => navigate('/video-meeting')}
            >
              <span className="action-icon">🎥</span>
              <span className="action-text">Join Video Meeting</span>
            </button>

            <button
              className="quick-action-btn"
              onClick={() => navigate('/flashcards')}
            >
              <span className="action-icon">➕</span>
              <span className="action-text">Create Flashcards</span>
            </button>
          </div>
        </section>
      </div>
    </div>
  );
};

export default ProgressPage;
